import "./styles.css";
import { App } from "./app/app";
import { tr } from "./i18n/tr";

const root = document.getElementById("app");
if (!root) throw new Error("App root is missing.");

new App(root).start();

if (import.meta.env.PROD && "serviceWorker" in navigator) {
  const notice = document.createElement("aside");
  notice.className = "pwa-notice";
  notice.hidden = true;
  notice.setAttribute("role", "status");
  notice.setAttribute("aria-live", "polite");
  notice.setAttribute("aria-atomic", "true");
  document.body.append(notice);

  let registration: ServiceWorkerRegistration | null = null;
  let waitingWorker: ServiceWorker | null = null;
  let applyingWorker: ServiceWorker | null = null;
  let reloading = false;
  let reloadAfterGame = false;
  let hasController = Boolean(navigator.serviceWorker.controller);
  const observedWorkers = new WeakSet<ServiceWorker>();
  const renderNotice = () => {
    notice.replaceChildren();
    if (!navigator.onLine) {
      notice.textContent = tr.offlineShell;
      notice.hidden = false;
      return;
    }
    if (document.body.dataset.urlcadeScreen === "game") {
      notice.hidden = true;
      return;
    }
    if (applyingWorker) {
      const message = document.createElement("span");
      message.textContent = tr.updateApplying;
      notice.append(message);
      notice.hidden = false;
      return;
    }
    if (waitingWorker) {
      const worker = waitingWorker;
      const message = document.createElement("span");
      message.textContent = tr.updateReady;
      const button = document.createElement("button");
      button.className = "button button-small button-primary";
      button.type = "button";
      button.textContent = tr.updateNow;
      button.addEventListener("click", () => {
        if (waitingWorker !== worker || applyingWorker || worker.state !== "installed") return;
        applyingWorker = worker;
        renderNotice();
        try {
          worker.postMessage({ type: "SKIP_WAITING" });
        } catch {
          applyingWorker = null;
          reconcileRegistration();
        }
      }, { once: true });
      notice.append(message, button);
      notice.hidden = false;
      return;
    }
    notice.hidden = true;
  };

  const reconcileRegistration = () => {
    if (applyingWorker?.state === "redundant") applyingWorker = null;
    const worker = registration?.waiting;
    waitingWorker = worker?.state === "installed" && navigator.serviceWorker.controller ? worker : null;
    if (waitingWorker) observeWorker(waitingWorker);
    renderNotice();
  };
  const observeWorker = (worker: ServiceWorker) => {
    if (observedWorkers.has(worker)) return;
    observedWorkers.add(worker);
    worker.addEventListener("statechange", () => {
      if (applyingWorker === worker && worker.state === "redundant") applyingWorker = null;
      const registeredWaiting = registration?.waiting;
      waitingWorker = registeredWaiting?.state === "installed" && navigator.serviceWorker.controller
        ? registeredWaiting
        : worker.state === "installed" && navigator.serviceWorker.controller
          ? worker
          : null;
      if (waitingWorker) observeWorker(waitingWorker);
      renderNotice();
    });
  };

  window.addEventListener("online", renderNotice);
  window.addEventListener("offline", renderNotice);
  window.addEventListener("urlcade:screenchange", () => {
    renderNotice();
    if (reloadAfterGame && document.body.dataset.urlcadeScreen !== "game" && !reloading) {
      reloading = true;
      window.location.reload();
    }
  });
  window.addEventListener("urlcade:localechange", renderNotice);
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (applyingWorker && navigator.serviceWorker.controller === applyingWorker) applyingWorker = null;
    if (!hasController) {
      hasController = true;
      reconcileRegistration();
      return;
    }
    if (reloading) {
      reconcileRegistration();
      return;
    }
    if (document.body.dataset.urlcadeScreen === "game") {
      reloadAfterGame = true;
      reconcileRegistration();
      return;
    }
    reconcileRegistration();
    reloading = true;
    window.location.reload();
  });

  void navigator.serviceWorker.register("/sw.js").then((registered) => {
    registration = registered;
    registered.addEventListener("updatefound", () => {
      const candidate = registered.installing;
      if (candidate) observeWorker(candidate);
    });
    if (registered.installing) observeWorker(registered.installing);
    reconcileRegistration();
    window.addEventListener("focus", () => { void registered.update(); });
  }).catch(() => {
    // The product remains usable when the browser declines service worker registration.
  });
  renderNotice();
}
